import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { combineLatest, Subject } from 'rxjs';
import { DraftFacade } from '@classifieds-sell/state';
import { FormHelperService } from '../../../helpers/form-helper.service';
import { PropertySectionBase } from '../../property-section-base';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PROPERTY_UI_COMPACT_MAX_WIDTH } from '../../../property-ui-constants';
import { CategoryEnum, Fee, Photo, Price } from 'libs/datalayer/src';
import { StepIds } from '../../../models/property-step/property-step-model';
import {
    getListingPrice,
    getPackagePrice
} from '../../../helpers/price.helper';
import {
    getPromoteFooterNotes,
    getPromotePageSubTitle
} from '../../../helpers/promote.helper';
import { FeeCalculationFacade } from '../../../state/fee-calculation/fee-calculation.facade';

@Component({
    selector: 'property-promote-section',
    templateUrl: './property-promote-section.component.html',
    styleUrls: ['./property-promote-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PropertyPromoteSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('propertyPromoteSectionForm')
    propertyPromoteSectionForm: NgForm;

    pageSubTitle: string;
    pageFooterNotes: string;

    fees: any;
    selectedBundle: Fee;
    originalFees: any;
    reversedFees: any;
    distinctPhotos: Photo[];
    photoList: Photo[];
    modalPhotos: Photo[];
    listingPrice: number | null;
    showModal: boolean;
    packageName: string;
    displayError: boolean;
    isMobile: boolean;
    stepIds = StepIds;
    zeroChargeMessage: string;
    originalCategoryPackageId: number;
    superFeatureFee = 0;
    isAllowDisplayReviewPromotionSection = false;
    showFeesInformation = false;

    constructor(
        public draftFacade: DraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private _breakpointObserver: BreakpointObserver,
        public feeCalculationFacade: FeeCalculationFacade
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this.distinctPhotos = [];
        this.photoList = [];
        this.fees = [];
        this.showModal = false;
        this.selectedBundle = {
            id: 0,
            name: '',
            description: '',
            feePrice: 0,
            photos: [],
            details: [],
            prices: [],
            minPhotoRequired: 0
        };

        this.zeroChargeMessage = 'Your promotion';
        super.onInit(() => {
            this.showFeesInformation = this.categoryId === CategoryEnum.residentialForSale && !this.draftData.propertyInformation.rateableValue;
            this.pageSubTitle = getPromotePageSubTitle(this.categoryId);
            this.pageFooterNotes = getPromoteFooterNotes(this.categoryId);
            this.originalFees = this.metadata.fees;
            this.reversedFees = this.originalFees.slice().reverse();
            this.listingPrice = getListingPrice(
                this.categoryId,
                this.draftData,
                this.metadata
            );
            this.originalFees.forEach((element: any) => {
                element.photos.forEach((item: any) => {
                    this.photoList.push(item);
                });
            });

            this.photoList.forEach((photoItem: Photo) => {
                if (!this.distinctPhotos.find((i) => i.id === photoItem.id)) {
                    this.distinctPhotos.push(
                        JSON.parse(JSON.stringify(photoItem))
                    );
                }
            });

            this.initializeSelectedBundle();
            this._changeDetectorRef.markForCheck();
        });
        this.feeCalculationFacade.packagePrices$
            .subscribe((fees) => {
                this.fees = fees;
                this.reversedFees = fees.slice().reverse();
            });
        this.feeCalculationFacade.superFeatureInfo$
            .subscribe((superFeatureInfo) => {
                this.isAllowDisplayReviewPromotionSection = superFeatureInfo.isSuperFeatureExpired;
                this.superFeatureFee = superFeatureInfo.superFeatureRepurchaseFee;
            });
        combineLatest([this._breakpointObserver
            .observe([PROPERTY_UI_COMPACT_MAX_WIDTH]), this.feeCalculationFacade.packagePrices$])
            .subscribe(([result, calculatedFees]) => {
                if (result.matches) {
                    this.fees = calculatedFees.slice().reverse();
                    this.isMobile = true;
                } else {
                    this.fees = calculatedFees;
                    this.isMobile = false;
                }

                this._changeDetectorRef.markForCheck();
            });
    }

    public isDowngradedPackage(premiumPackageId: number) {
        return premiumPackageId < this.originalDraftData.propertyPromote.categoryPackageId;
    }

    handleChangeCategoryPackageId(): void {
        if (!this.isAllowDisplayReviewPromotionSection && this.draftData.propertyPromote.hasSuperFeature !== null) {
            this.draftData.propertyPromote.hasSuperFeature = null;
            this.publish();
        }
    }

    getPrice(prices: Price[]): number | null {
        return getPackagePrice(prices, this.listingPrice);
    }

    openModal(photos: Photo[], name: string): void {
        this.modalPhotos = photos;
        this.showModal = true;
        this.packageName = name;
        this._changeDetectorRef.markForCheck();
    }

    setPhotos(packageId: number): void {
        if (packageId && packageId > 0) {
            if (
                this.draftData.propertyPromote.categoryPackageId !== packageId
            ) {
                this.propertyPromoteSectionForm?.form.markAsDirty();
            }
            this.draftData.propertyPromote.categoryPackageId = packageId;
            this.selectedBundle = this.findFee(packageId);
            this.checkMinPhotoRequirement();
            if (this.selectedBundle && this.selectedBundle.photos) {
                this.clearPhotoSelectionAndDescription();
                this.setPhotoSelectionAndDescription();
            }
            this._changeDetectorRef.markForCheck();
        }
    }

    findFee(feeId: number): Fee {
        return this.originalFees.find((i: Fee) => i.id === feeId);
    }

    clearPhotoSelectionAndDescription(): void {
        if (this.distinctPhotos) {
            this.distinctPhotos.forEach((element: Photo) => {
                element.description = '';
                element.selected = false;
            });
        }
    }

    setPhotoSelectionAndDescription(): void {
        this.selectedBundle.photos.forEach((element: Photo) => {
            const objIndex = this.distinctPhotos.findIndex(
                (obj) => obj.id === element.id
            );
            this.distinctPhotos[objIndex].description = element.description;
            this.distinctPhotos[objIndex].selected = true;
        });
    }

    initializeSelectedBundle() {
        if (this.draftData?.propertyPromote.categoryPackageId) {
            this.selectedBundle = this.findFee(
                this.draftData.propertyPromote.categoryPackageId
            );
            this.setPhotos(this.draftData?.propertyPromote.categoryPackageId);
        }
    }

    isValid(): boolean {
        if (this.displayError) {
            this.propertyPromoteSectionForm?.form?.controls?.categoryPackageId?.setErrors(
                { incorrect: true }
            );
        }
        this._formHelperService.validate(this.propertyPromoteSectionForm);
        return this.propertyPromoteSectionForm.form.valid;
    }

    dirty(): boolean {
        return this.propertyPromoteSectionForm.form.value.categoryPackageId !== null;
    }

    checkMinPhotoRequirement(): void {
        const totalPhotos =
            this.draftData.propertyPhotos?.photos === null
                ? 0
                : this.draftData.propertyPhotos.photos.length;
        if (
            this.selectedBundle.minPhotoRequired === 0 ||
            this.selectedBundle.minPhotoRequired === null ||
            this.selectedBundle.minPhotoRequired <= totalPhotos
        ) {
            this.displayError = false;
        } else {
            this.displayError = true;
        }
    }
}
